import React, { useState, useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleReviewsBanner = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState({
    show_reviews: true,
    reviews_rating: '5.0',
    reviews_count: '47',
    reviews_url: 'https://maps.app.goo.gl/GPaMHSSiUcEEy97D6'
  });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, []);

  // Don't render if reviews are hidden
  if (settings.show_reviews === false) {
    return null;
  }

  const rating = parseFloat(settings.reviews_rating) || 5.0;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <section className="bg-gradient-to-r from-slate-100 to-slate-50 border-y border-slate-200">
      <div className="container mx-auto px-6 py-8">
        <a 
          href={settings.reviews_url || 'https://maps.app.goo.gl/GPaMHSSiUcEEy97D6'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col sm:flex-row items-center justify-center gap-6 group"
        >
          {/* Google Logo */}
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-slate-700 font-medium text-lg">
              {language === 'ar' ? 'تقييمات Google' : 'Google Reviews'}
            </span>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-3">
            {/* Stars */}
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={20} 
                  className={i < fullStars ? 'text-amber-400 fill-amber-400' : (i === fullStars && hasHalfStar) ? 'text-amber-400 fill-amber-400/50' : 'text-slate-300'}
                />
              ))}
            </div>
            
            {/* Rating number */}
            <span className="text-2xl font-bold text-slate-800">{settings.reviews_rating}</span>
            
            {/* Review count */}
            <span className="text-slate-500 text-sm">
              ({settings.reviews_count} {language === 'ar' ? 'تقييم' : language === 'en' ? 'reviews' : 'avis'})
            </span>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
            <span className="text-sm font-medium">
              {language === 'ar' ? 'اقرأ تقييماتنا' : language === 'en' ? 'Read our reviews' : 'Lire nos avis'}
            </span>
            <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </a>
      </div>
    </section>
  );
};

export default GoogleReviewsBanner;
